/* eslint-disable react-hooks/exhaustive-deps */
import fundingSvc from '@efex-services/funds/fundingSvc';
import rateSvc from '@efex-services/rate/rateSvc';
import { ExchangeResponse, IExchange } from '@efex-services/rate/types';
import useImmediateApiCall from '@main-hooks/useImmediateApiCall';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { selectBusiness } from '@pages/profile/store/selectors';
import { camelCaseMapData } from '@utils/formater';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import { Button } from '@efexteam/components';
import UploadFileInput from '../../../common/Form/UploadFileInput';
import DetailView from '../components/DetailView';
import { useFormData } from '../context';
import { isUnsupportedClabe } from '../utils';

interface DetailsProps {
  nextFormStep: () => void;
  prevFormStep: () => void;
}

type FormData = {
  comments: string;
  paymentReason: string;
};

const Details = ({ nextFormStep, prevFormStep }: DetailsProps) => {
  const { t } = useTranslation();
  const company = useSelector(selectBusiness);
  const [fileData, setFileData] = useState<File[] | undefined>(undefined);

  const { setFormValues, data } = useFormData();
  const { client: clientSelected } = data;

  const hasUnsupportedClabe = isUnsupportedClabe(
    clientSelected?.bankAccount?.clabe
  );

  const { data: exchangeData, invoke: getExchange } = useApiCall(() =>
    rateSvc
      .getExchange({
        currency: data.currency,
        desCurrency: data.clientCurrency,
        enterpriseId: company?.enterpriseId || '0',
      })
      .then((response) =>
        camelCaseMapData<ExchangeResponse, IExchange>(response)
      )
  );
  const { data: paymentReasons } = useImmediateApiCall(() =>
    fundingSvc.getPaymentReasons()
  );

  const handleChangeFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length) {
      const filesArray: File[] = [];
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index <= files.length - 1; index++) {
        filesArray[index] = files.item(index) as File;
      }
      setFileData(filesArray);
      setFormValues({ ...data, invoices: filesArray });
      return null;
    }
    setFileData(undefined);
    setFormValues({ ...data, invoices: undefined });
    return null;
  };

  const handleDeleteFile = (files: File[]) => {
    setFileData(files);
    setFormValues({ ...data, invoices: files });
  };

  const {
    handleSubmit,
    setValue,
    register,
    formState: { errors },
    control,
  } = useForm<FormData>();

  const onSubmit = handleSubmit((values) => {
    setFormValues({
      ...data,
      comments: values.comments,
      paymentReason: values.paymentReason,
    });
    nextFormStep();
  });

  useEffect(() => {
    if (data.paymentReason) {
      setValue('paymentReason', data.paymentReason, { shouldValidate: true });
    }
    if (data.comments) {
      setValue('comments', data.comments);
    }
    if (data.invoices && data.invoices.length > 0) {
      setFileData(data.invoices);
    }
  }, [data, setValue]);

  useEffect(() => {
    if (
      (!data.rateData || !data.exchangeData) &&
      data.currency !== data.clientCurrency
    ) {
      getExchange();
    }
  }, [data]);

  useEffect(() => {
    if (exchangeData) {
      setFormValues({
        ...data,
        rateData: exchangeData,
        exchangeData: {
          currency: data.currency,
          clientCurrency: data.clientCurrency,
          exchangeData,
        },
      });
    }
  }, [exchangeData]);

  if (!clientSelected) return null;

  return (
    <>
      <Typography variant="h6" className="!my-4 text-center">
        {t('detail')}
      </Typography>
      <DetailView />
      <form onSubmit={onSubmit} autoComplete="off">
        <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
          <Grid item xs={12}>
            <UploadFileInput
              onChange={handleChangeFiles}
              files={fileData}
              title={t('uploadInvoiceSendPayment') || ''}
              onDelete={handleDeleteFile}
              showDesktopMessage
              onPreventUpload={() => {}}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} className="!mx-auto">
            <FormControl fullWidth>
              <InputLabel id="paymentReason">{t('paymentReason')}</InputLabel>
              <Controller
                name="paymentReason"
                rules={{ required: true }}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select
                    labelId="paymentReason"
                    id="paymentReason"
                    error={!!errors.paymentReason}
                    label={t('paymentReason')}
                    size="medium"
                    MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                    value={value}
                    onChange={onChange}
                  >
                    {paymentReasons?.map((reason) => (
                      <MenuItem key={reason.id} value={reason.id}>
                        {t(reason.name)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.paymentReason && (
                <p className="text-orange-600 text-xs">{t('defaultError')}</p>
              )}
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            className="!mx-auto !mt-3 flex !flex-col items-center justify-center"
          >
            <TextField
              fullWidth
              id="comments"
              className="max-w-[280px] w-full"
              label={t('comment')}
              multiline
              error={!!errors.comments}
              rows={4}
              {...register('comments', {
                pattern: {
                  value: /^[ -~\r\n]+$/,
                  message: '',
                },
                maxLength: 140,
              })}
            />
            {errors.comments && errors.comments.type === 'pattern' && (
              <p className="text-orange-600 text-xs">
                {t('ccErrorNoSpecialCharacters')}
              </p>
            )}
            {errors.comments && errors.comments.type === 'maxLength' && (
              <p className="text-orange-600 text-xs">
                {t('MaxLengthCharacters', { max: 140 })}
              </p>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            className="flex !flex-col md:!flex-row justify-between gap-4"
          >
            <Button
              appearance="secondary"
              size="large"
              className="mt-4"
              onClick={prevFormStep}
            >
              {t('back')}
            </Button>
            <Button
              disabled={
                !data.amount ||
                data.amount === '0' ||
                !data.resultAmount ||
                data.resultAmount === '0' ||
                hasUnsupportedClabe
              }
              appearance="primary"
              size="large"
              className="mt-4"
              type="submit"
            >
              {t('continue')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default Details;
